import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import axios from 'axios';
import { FormattedMessage as FM } from 'react-intl';

import { closeModal, openModal } from 'interface/user/_redux/actions';
import {
  PageLayout,
  PageTabs,
  TranslatedNavLink,
  PageContentPreloader,
  Calendar,
  BasicModal,
  NoItemsPanel,
  PasswordChangeForm,
} from 'common/components';
import noOrdersImage from 'common/images/noOrders.svg';
import { withTheme } from 'common/styling/theme';
import { changeTradingAccountPassword } from '../_redux';
import { getTradingAccount, getTradingAccountOperations } from '../../_redux/actions';

import TradeOperationsTable from '../../_components/TradeOperationsTable';
import AccountInfoPanel from '../../_components/AccountInfoPanel';
import { AccountLeverageChangeForm } from '../_components';

import { staticStyles, getDynamicStyles } from './style';

const startDate = moment().add(-6, 'day').startOf('day').unix();

const endDate = moment().add(1, 'day').startOf('day').unix();

const startCalendar = moment().add(-6, 'day').startOf('day');

const endCalendar = moment().add(1, 'day').startOf('day');

const AccountPage = ({
  account,
  getTradingAccount,
  getOperations,
  operationsAreLoaded,
  operations,
  history,
  match,
  accountIsLoaded,
  modals,
  openModal,
  closeModal,
  openOrders,
  closedOrders,
  delayedOrders,
  equityExist,
  redirect,
  theme,
}) => {
  const { accountId } = match.params;
  const errorCountRef = useRef(0);
  const [openTradesData, setOpenTradesData] = useState(null);

  useEffect(() => {
    getTradingAccount(accountId);
  }, [getTradingAccount, accountId]);

  // 如果没有operationStatus，重定向到closed
  useEffect(() => {
    if (accountIsLoaded && !match.params.operationStatus) {
      history.replace(`/account/${accountId}/closed`);
    }
  }, [accountIsLoaded, match.params.operationStatus, accountId, history]);

  const isMt5Account = account.platformSlug === 'mt5';

  const OPERATION_STATUSES_BY_SLUG = {
    cfh: 'closed',
    mt5: 'closed',
  };

  const getOperationStatus = useCallback(
    slug => match.params.operationStatus ?? OPERATION_STATUSES_BY_SLUG[slug],
    [OPERATION_STATUSES_BY_SLUG, match.params.operationStatus]
  );

  const convertedPendingStatus = isMt5Account ? 'deferred' : 'delayed';

  const operationStatus = useMemo(() => {
    const status = getOperationStatus(account.platformSlug);
    // 默认为closed (Deals)
    if (!status) return 'closed';
    return status === 'pending' ? convertedPendingStatus : status;
  }, [getOperationStatus, account.platformSlug, convertedPendingStatus]);

  const memoizedOnChangeParams = useMemo(
    () => ({ id: Number(accountId), operationStatus }),
    [accountId, operationStatus]
  );

  useEffect(() => {
    if (accountIsLoaded) getOperations(accountId, operationStatus, startDate, endDate);
  }, [getOperations, accountId, operationStatus, accountIsLoaded]);

  // Open Orders 每秒刷新
  useEffect(() => {
    // 进入Open Orders页面时重置错误计�?
    errorCountRef.current = 0;

    if (accountIsLoaded && operationStatus === 'open') {
      const fetchOpenTrades = async () => {
        // 检查是否超�?次错�?
        if (errorCountRef.current >= 5) {
          console.error('连续5次请求失败，停止刷新');
          return;
        }

        try {
          const response = await axios.get(`/platforms/account/${accountId}/open_trades/`);
          // 检查HTTP状态码
          if (response.status === 400 || !response.data || !response.data.trades) {
            console.error('Request failed or no data:', response.status, response.data);
            errorCountRef.current += 1;
            console.error('Error count updated to:', errorCountRef.current);
            if (errorCountRef.current >= 5) {
              console.error('连续5次请求失败，停止刷新');
            }
            return;
          }

          // 请求成功，重置错误计�?
          errorCountRef.current = 0;
          // 更新本地状�?
          setOpenTradesData(response.data);
        } catch (error) {
          console.error('Failed to fetch open trades:', error);
          console.error('Error status:', error.response?.status);
          console.error('Error data:', error.response?.data);
          errorCountRef.current += 1;
          console.error('Error count updated to:', errorCountRef.current);
          if (errorCountRef.current >= 5) {
            console.error('连续5次请求失败，停止刷新');
          }
        }
      };

      // 首次加载
      fetchOpenTrades();

      // �?秒刷�?
      const intervalId = setInterval(() => {
        if (errorCountRef.current < 5) {
          fetchOpenTrades();
        } else {
          clearInterval(intervalId);
        }
      }, 5000);

      // 清理定时�?
      return () => clearInterval(intervalId);
    }
  }, [accountIsLoaded, operationStatus, accountId, getOperations, startDate, endDate]);

  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const handleDeposit = () => {
    history.push(`/deposit/${accountId}`);
  };

  const handleWithdraw = () => {
    history.push(`/withdraw/${accountId}`);
  };

  useEffect(() => {
    if (redirect) {
      window.location = '/';
    }
  }, [redirect]);

  return (
    <PageLayout
      captionSlug="clientAccount"
      extraCaptionText={account && account.login ? ` #${account.login}` : ''}
      showReload
      onReloadClick={() => getTradingAccount(accountId)}
      reloading={!accountIsLoaded}>
      <PageContentPreloader ready={accountIsLoaded}>
        {!!account.id && (
          <AccountInfoPanel
            {...account}
            onLeverageChangeClick={() => openModal('accountChangeLeverage')}
            onPasswordChangeClick={() => openModal('accountChangePassword')}
            onDepositClick={handleDeposit}
            onWithdrawClick={handleWithdraw}
            modals={modals}
            closeModal={closeModal}
            openModal={openModal}
            isBinary={account.platformTitle === 'Binary Options'}
          />
        )}

        {equityExist && (
          <>
            <div className="AccountPage__top-line">
              <PageTabs>
                {closedOrders && <TranslatedNavLink id="justDealOrders" to={`/account/${accountId}/closed`} />}
                {openOrders && <TranslatedNavLink id="justOpenOrders" to={`/account/${accountId}/open`} />}
                {false && delayedOrders && <TranslatedNavLink id="justPending" to={`/account/${accountId}/pending`} />}
              </PageTabs>
              <div className="AccountPage__date">
                {operationStatus !== 'delayed' && operationStatus !== 'open' && (
                  <>
                    <span className="AccountPage__calendar-label">
                      {operationStatus === 'closed' ? (
                        <FM id="justCloseTime">{txt => txt}</FM>
                      ) : (
                        <FM id="justOpenTime">{txt => txt}</FM>
                      )}
                    </span>
                    <Calendar
                      onChangeFunc={getOperations}
                      onChangeParams={memoizedOnChangeParams}
                      defaultStartDate={startCalendar}
                      defaultEndDate={endCalendar}
                    />
                  </>
                )}
              </div>
            </div>
            <div className="AccountPage__table">
              <PageContentPreloader ready={operationStatus === 'open' || operationsAreLoaded} type="smallLines">
                {operationStatus === 'open' && openTradesData ? (
                  openTradesData.trades?.length ? (
                    <TradeOperationsTable
                      data={openTradesData.trades}
                      summary={openTradesData.summary}
                      operationStatus={operationStatus}
                      isClient
                    />
                  ) : (
                    <NoItemsPanel icon={noOrdersImage} captionSlug="yotHaveNoOrdersYet" />
                  )
                ) : operations?.trades?.length ? (
                  <TradeOperationsTable
                    data={operations.trades}
                    summary={operations.summary}
                    operationStatus={operationStatus}
                    isClient
                  />
                ) : (
                  <NoItemsPanel icon={noOrdersImage} captionSlug="yotHaveNoOrdersYet" />
                )}
              </PageContentPreloader>
            </div>
          </>
        )}
      </PageContentPreloader>
      <BasicModal
        captionId="authChangePassword"
        isOpen={modals.accountChangePassword}
        onRequestClose={() => closeModal('accountChangePassword')}>
        {modals.accountChangePassword && (
          <PasswordChangeForm
            id={accountId}
            changePasswordFunc={changeTradingAccountPassword}
            onSubmitSuccess={() => closeModal('accountChangePassword')}
          />
        )}
      </BasicModal>
      <BasicModal
        captionId="changeLeverage"
        isOpen={modals.accountChangeLeverage}
        onRequestClose={() => closeModal('accountChangeLeverage')}>
        {modals.accountChangeLeverage && (
          <AccountLeverageChangeForm
            accountId={accountId}
            options={account.availableLeverages}
            onSubmitSuccess={() => {
              closeModal('accountChangeLeverage');
              getTradingAccount(accountId);
            }}
            initialValues={{ leverage: account.leverage }}
          />
        )}
      </BasicModal>
      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </PageLayout>
  );
};

AccountPage.propTypes = {
  account: PropTypes.object.isRequired,
  operationsAreLoaded: PropTypes.bool.isRequired,
  operations: PropTypes.object,
  getTradingAccount: PropTypes.func.isRequired,
  getOperations: PropTypes.func.isRequired,
  openOrders: PropTypes.bool.isRequired,
  closedOrders: PropTypes.bool.isRequired,
  delayedOrders: PropTypes.bool.isRequired,
  accountIsLoaded: PropTypes.bool.isRequired,
  equityExist: PropTypes.bool.isRequired,
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  modals: PropTypes.object.isRequired,
  openModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  redirect: PropTypes.bool.isRequired,
  theme: PropTypes.object,
};

AccountPage.defaultProps = {
  operations: {},
  theme: {},
};

export default compose(
  withRouter,
  withTheme(),
  connect(
    state => {
      const { openOrders, closedOrders, delayedOrders } = state.interfaceConfig.equityTradingHistoryDisplay;

      const equityExist = [openOrders, closedOrders, delayedOrders].some(display => display);

      const account = state.client.common.currentAccount;
      const accountIsLoaded = state.client.common.currentAccountIsLoaded;

      const operations = state.client.common.currentAccountOperations;
      const operationsAreLoaded = state.client.common.currentAccountOperationsAreLoaded;

      return {
        account,
        accountIsLoaded,
        operations,
        operationsAreLoaded,
        openOrders,
        closedOrders,
        delayedOrders,
        equityExist,
        modals: state.user.modals,
        redirect: state.client.common.redirect,
      };
    },
    {
      getTradingAccount: id => getTradingAccount.request(id),
      getOperations: (id, operationStatus, startDate, endDate) =>
        getTradingAccountOperations.request(id, operationStatus, startDate, endDate),
      openModal: name => openModal(name),
      closeModal: name => closeModal(name),
    }
  )
)(AccountPage);
export { AccountPage };
